import React, { useState, useRef } from "react";
import {
  YStack,
  XStack,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner,
  Input,
  ScrollView
} from "tamagui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Linking, Dimensions, FlatList, TextInput, Platform, Alert } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";
import HuggingFaceExportModal, { HuggingFaceExportModalHandle } from "@components/modals/HuggingFaceExportModal/HuggingFaceExportModal";
import { useDispatch } from "react-redux";
import { ADD_INTEGRATION, REMOVE_INTEGRATION } from "@reduxLocal/actions/types";

const HuggingFaceOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const exportModalRef = useRef<HuggingFaceExportModalHandle>(null);
  
  const [tokenInput, setTokenInput] = useState('');
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenError, setTokenError] = useState('');

  // Query for HF profile - enable if we might have a token
  const { data: profileData, isLoading: profileLoading, error: profileError, refetch: refetchProfile } = useQuery({
    queryKey: ['huggingFaceProfile'],
    queryFn: async () => await apis.getHuggingFaceProfile(),
    enabled: true, // Try to fetch on mount to check if already connected
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  // Query for HF models
  const { data: modelsData, isLoading: modelsLoading, refetch: refetchModels } = useQuery({
    queryKey: ['huggingFaceModels'],
    queryFn: async () => await apis.getHuggingFaceModels(),
    enabled: !!profileData?.username,
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  // Query for training history
  const { data: trainingHistory, isLoading: historyLoading, refetch: refetchHistory } = useQuery({
    queryKey: ['huggingFaceTrainingHistory'],
    queryFn: async () => await apis.getHuggingFaceTrainingHistory(),
    enabled: !!profileData?.username,
    staleTime: 1000 * 60 * 2, // Refresh more frequently for training status
    retry: 2,
  });

  // Mutation to store token
  const storeTokenMutation = useMutation({
    mutationFn: async (token: string) => {
      return await apis.storeHuggingFaceToken(token);
    },
    onSuccess: (data) => {
      setTokenInput('');
      setShowTokenInput(false);
      setTokenError('');
      // Update Redux store to reflect connection
      dispatch({ 
        type: ADD_INTEGRATION, 
        payload: { 
          authType: 'huggingface', 
          data: {
            token: true, // Don't store actual token in Redux
            username: data.profile?.username,
            email: data.profile?.email,
            tokenStoredAt: new Date().toISOString()
          }
        } 
      });
      queryClient.invalidateQueries({ queryKey: ['huggingFaceProfile'] });
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
      refetchProfile();
    },
    onError: (error: any) => {
      console.error('[HF Options] Token storage error:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.details || error?.message || 'Failed to store token. Please check your token and try again.';
      setTokenError(errorMessage);
      
      // Show alert with detailed error
      if (error?.response?.status === 400) {
        Alert.alert(
          'Invalid Token',
          errorMessage + '\n\nPlease ensure:\n• Your token is valid and active\n• The token has "read" permissions\n• You copied the full token (starts with hf_)',
          [{ text: 'OK' }]
        );
      }
    }
  });

  // Mutation to remove token
  const removeTokenMutation = useMutation({
    mutationFn: async () => {
      return await apis.removeHuggingFaceToken();
    },
    onSuccess: () => {
      // Remove from Redux store
      dispatch({ 
        type: REMOVE_INTEGRATION, 
        payload: 'huggingface'
      });
      queryClient.invalidateQueries({ queryKey: ['huggingFaceProfile'] });
      queryClient.invalidateQueries({ queryKey: ['huggingFaceModels'] });
      queryClient.invalidateQueries({ queryKey: ['huggingFaceTrainingHistory'] });
      queryClient.invalidateQueries({ queryKey: ['integrationData'] });
    }
  });

  const handleStoreToken = () => {
    if (!tokenInput.trim()) {
      setTokenError('Please enter a valid Hugging Face API token');
      return;
    }
    setTokenError('');
    storeTokenMutation.mutate(tokenInput.trim());
  };

  const handleRemoveToken = () => {
    removeTokenMutation.mutate();
  };

  const handleExportData = () => {
    exportModalRef.current?.openModal();
  };

  // Calculate training statistics
  const trainingStats = React.useMemo(() => {
    if (!trainingHistory?.trainings) return null;
    
    const trainings = trainingHistory.trainings || [];
    const totalTrainings = trainings.length;
    const completedTrainings = trainings.filter((t: any) => t.status === 'completed').length;
    const failedTrainings = trainings.filter((t: any) => t.status === 'failed').length;
    const activeTrainings = trainings.filter((t: any) => t.status === 'training' || t.status === 'pending').length;
    const successRate = totalTrainings > 0 ? Math.round((completedTrainings / totalTrainings) * 100) : 0;
    const lastTraining = trainings.length > 0 ? trainings[0] : null;

    return {
      totalTrainings,
      completedTrainings,
      failedTrainings,
      activeTrainings,
      successRate,
      lastTraining
    };
  }, [trainingHistory]);

  const profile = profileData?.profile || {};
  const models = modelsData?.models || [];
  const trainings = trainingHistory?.trainings || [];

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {profileError && !profileData && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to connect to Hugging Face
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please check your API token and try again
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetchProfile()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Token Input Section - Show if not connected */}
        {!profileData?.username && !profileLoading && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack gap="$4">
                <H4
                  color={theme ? "#1e293b" : "#f1f5f9"}
                  fontWeight="700"
                >
                  Connect Hugging Face
                </H4>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  Enter your Hugging Face API token to connect your account. You can find your token in your Hugging Face settings.
                </SizableText>
                
                {!showTokenInput ? (
                  <Button
                    backgroundColor="#FFD21E"
                    color="#000000"
                    onPress={() => setShowTokenInput(true)}
                    borderRadius={12}
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="vpn-key" size={18} color="#000000" />
                      <SizableText color="#000000" fontWeight="600" size="$4">
                        Enter API Token
                      </SizableText>
                    </XStack>
                  </Button>
                ) : (
                  <YStack gap="$3">
                    <YStack gap="$2">
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                      >
                        Hugging Face API Token
                      </SizableText>
                      <Input
                        placeholder="hf_..."
                        value={tokenInput}
                        onChangeText={(text) => {
                          setTokenInput(text);
                          setTokenError('');
                        }}
                        secureTextEntry
                        backgroundColor={theme ? "#f8fafc" : "#334155"}
                        borderColor={tokenError ? "#ef4444" : (theme ? "#e2e8f0" : "#475569")}
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontSize="$4"
                        padding="$3"
                        borderRadius={8}
                      />
                      {tokenError && (
                        <SizableText size="$3" color="#ef4444">
                          {tokenError}
                        </SizableText>
                      )}
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Your token is encrypted and stored securely. Get your token from{" "}
                        <SizableText
                          size="$2"
                          color="#FFD21E"
                          textDecorationLine="underline"
                          onPress={() => Linking.openURL('https://huggingface.co/settings/tokens')}
                        >
                          Hugging Face Settings
                        </SizableText>
                      </SizableText>
                    </YStack>
                    <XStack gap="$3">
                      <Button
                        flex={1}
                        backgroundColor={theme ? "#f1f5f9" : "#334155"}
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        onPress={() => {
                          setShowTokenInput(false);
                          setTokenInput('');
                          setTokenError('');
                        }}
                        borderRadius={12}
                        paddingVertical="$3"
                      >
                        <SizableText fontWeight="600" size="$4">
                          Cancel
                        </SizableText>
                      </Button>
                      <Button
                        flex={1}
                        backgroundColor="#FFD21E"
                        color="#000000"
                        onPress={handleStoreToken}
                        borderRadius={12}
                        paddingVertical="$3"
                        disabled={storeTokenMutation.isPending}
                      >
                        {storeTokenMutation.isPending ? (
                          <Spinner size="small" color="#000000" />
                        ) : (
                          <SizableText color="#000000" fontWeight="600" size="$4">
                            Connect
                          </SizableText>
                        )}
                      </Button>
                    </XStack>
                  </YStack>
                )}
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {profileLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#FFD21E" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading Hugging Face profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {profile?.username && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  <View style={{
                    width: 70,
                    height: 70,
                    borderRadius: 35,
                    backgroundColor: '#FFD21E',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <SizableText color="#000000" fontSize="$9" fontWeight="bold">
                      {profile.username?.charAt(0)?.toUpperCase() || 'H'}
                    </SizableText>
                  </View>

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                      {profile.username || "Hugging Face User"}
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={2}
                    >
                      {profile.email || "Connected to Hugging Face"}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Profile Details */}
                <YStack gap="$3">
                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#fef3c7" : "#78350f",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons name="account-circle" size={20} color="#FFD21E" />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Username
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? "#1e293b" : "#f1f5f9"}
                        fontWeight="600"
                        numberOfLines={1}
                      >
                        {profile.username}
                      </SizableText>
                    </YStack>
                  </XStack>

                  {profile.email && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#fef3c7" : "#78350f",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="email" size={20} color="#FFD21E" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Email
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {profile.email}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {profileData?.connectedDate && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#fef3c7" : "#78350f",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="event" size={20} color="#FFD21E" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Connected On
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {new Date(profileData.connectedDate).toLocaleDateString()}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  <XStack alignItems="center" gap="$3">
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#065f46",
                      borderRadius: 8,
                      padding: 8
                    }}>
                      <MaterialIcons
                        name={profileData?.authorised ? "check-circle" : "cancel"}
                        size={20}
                        color={profileData?.authorised ? "#10b981" : "#ef4444"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText
                        size="$2"
                        color={theme ? "#64748b" : "#94a3b8"}
                      >
                        Authorization Status
                      </SizableText>
                      <SizableText
                        size="$4"
                        color={profileData?.authorised ? "#10b981" : "#ef4444"}
                        fontWeight="600"
                      >
                        {profileData?.authorised ? "Authorized" : "Not Authorized"}
                      </SizableText>
                    </YStack>
                  </XStack>
                </YStack>

                {/* Action Buttons */}
                <XStack gap="$3" marginTop="$4">
                  <Button
                    flex={1}
                    backgroundColor="#FFD21E"
                    color="#000000"
                    onPress={handleExportData}
                    borderRadius={12}
                    paddingVertical="$3"
                  >
                    <XStack alignItems="center" gap="$2">
                      <MaterialIcons name="upload" size={18} color="#000000" />
                      <SizableText color="#000000" fontWeight="600" size="$4">
                        Export Data Model
                      </SizableText>
                    </XStack>
                  </Button>
                  <Button
                    backgroundColor={theme ? "#f1f5f9" : "#334155"}
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    onPress={async () => {
                      try {
                        await Linking.openURL(`https://huggingface.co/${profile.username}`);
                      } catch (error) {
                        console.error("Error opening Hugging Face profile:", error);
                      }
                    }}
                    borderRadius={12}
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                  >
                    <MaterialIcons name="open-in-new" size={18} color={theme ? "#1e293b" : "#f1f5f9"} />
                  </Button>
                  <Button
                    backgroundColor={theme ? "#fee2e2" : "#7f1d1d"}
                    color="#ef4444"
                    onPress={handleRemoveToken}
                    borderRadius={12}
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                    disabled={removeTokenMutation.isPending}
                  >
                    {removeTokenMutation.isPending ? (
                      <Spinner size="small" color="#ef4444" />
                    ) : (
                      <MaterialIcons name="logout" size={18} color="#ef4444" />
                    )}
                  </Button>
                </XStack>
              </Card>
            </Animatable.View>

            {/* Training Statistics */}
            {trainingStats && (
              <Animatable.View animation="fadeInUp" duration={600} delay={200}>
                <Card
                  backgroundColor={theme ? "#ffffff" : "#1e293b"}
                  borderColor={theme ? "#e2e8f0" : "#334155"}
                  borderRadius={16}
                  padding="$5"
                  elevate
                >
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                    marginBottom="$4"
                  >
                    Training Statistics
                  </H4>
                  <YStack gap="$3">
                    <XStack gap="$4" flexWrap="wrap">
                      <Card
                        backgroundColor={theme ? "#f0f9ff" : "#0c4a6e"}
                        padding="$4"
                        borderRadius={12}
                        flex={1}
                        minWidth={150}
                      >
                        <YStack gap="$2">
                          <SizableText
                            size="$2"
                            color={theme ? "#64748b" : "#94a3b8"}
                          >
                            Total Trainings
                          </SizableText>
                          <SizableText
                            size="$6"
                            color={theme ? "#0ea5e9" : "#38bdf8"}
                            fontWeight="700"
                          >
                            {trainingStats.totalTrainings}
                          </SizableText>
                        </YStack>
                      </Card>
                      <Card
                        backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                        padding="$4"
                        borderRadius={12}
                        flex={1}
                        minWidth={150}
                      >
                        <YStack gap="$2">
                          <SizableText
                            size="$2"
                            color={theme ? "#64748b" : "#94a3b8"}
                          >
                            Completed
                          </SizableText>
                          <SizableText
                            size="$6"
                            color={theme ? "#10b981" : "#34d399"}
                            fontWeight="700"
                          >
                            {trainingStats.completedTrainings}
                          </SizableText>
                        </YStack>
                      </Card>
                      <Card
                        backgroundColor={theme ? "#fef2f2" : "#450a0a"}
                        padding="$4"
                        borderRadius={12}
                        flex={1}
                        minWidth={150}
                      >
                        <YStack gap="$2">
                          <SizableText
                            size="$2"
                            color={theme ? "#64748b" : "#94a3b8"}
                          >
                            Failed
                          </SizableText>
                          <SizableText
                            size="$6"
                            color="#ef4444"
                            fontWeight="700"
                          >
                            {trainingStats.failedTrainings}
                          </SizableText>
                        </YStack>
                      </Card>
                      <Card
                        backgroundColor={theme ? "#fef3c7" : "#78350f"}
                        padding="$4"
                        borderRadius={12}
                        flex={1}
                        minWidth={150}
                      >
                        <YStack gap="$2">
                          <SizableText
                            size="$2"
                            color={theme ? "#64748b" : "#94a3b8"}
                          >
                            Success Rate
                          </SizableText>
                          <SizableText
                            size="$6"
                            color="#FFD21E"
                            fontWeight="700"
                          >
                            {trainingStats.successRate}%
                          </SizableText>
                        </YStack>
                      </Card>
                    </XStack>
                    {trainingStats.lastTraining && (
                      <XStack alignItems="center" gap="$3" marginTop="$2">
                        <View style={{
                          backgroundColor: theme ? "#fef3c7" : "#78350f",
                          borderRadius: 8,
                          padding: 8
                        }}>
                          <MaterialIcons name="schedule" size={20} color="#FFD21E" />
                        </View>
                        <YStack flex={1}>
                          <SizableText
                            size="$2"
                            color={theme ? "#64748b" : "#94a3b8"}
                          >
                            Last Training
                          </SizableText>
                          <SizableText
                            size="$4"
                            color={theme ? "#1e293b" : "#f1f5f9"}
                            fontWeight="600"
                          >
                            {new Date(trainingStats.lastTraining.startedAt).toLocaleDateString()} - {trainingStats.lastTraining.status}
                          </SizableText>
                        </YStack>
                      </XStack>
                    )}
                  </YStack>
                </Card>
              </Animatable.View>
            )}

            {/* Models List */}
            {models.length > 0 && (
              <Animatable.View animation="fadeInUp" duration={600} delay={300}>
                <Card
                  backgroundColor={theme ? "#ffffff" : "#1e293b"}
                  borderColor={theme ? "#e2e8f0" : "#334155"}
                  borderRadius={16}
                  padding="$5"
                  elevate
                >
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                    marginBottom="$4"
                  >
                    Your Models ({models.length})
                  </H4>
                  <ScrollView maxHeight={300}>
                    <YStack gap="$2">
                      {models.slice(0, 5).map((model: any, index: number) => (
                        <Card
                          key={index}
                          backgroundColor={theme ? "#f8fafc" : "#334155"}
                          padding="$3"
                          borderRadius={8}
                        >
                          <XStack alignItems="center" gap="$3">
                            <View style={{
                              backgroundColor: theme ? "#fef3c7" : "#78350f",
                              borderRadius: 8,
                              padding: 8
                            }}>
                              <MaterialIcons name="model-training" size={20} color="#FFD21E" />
                            </View>
                            <YStack flex={1}>
                              <SizableText
                                size="$3"
                                color={theme ? "#1e293b" : "#f1f5f9"}
                                fontWeight="600"
                              >
                                {model.id || model.name || 'Untitled Model'}
                              </SizableText>
                              {model.pipeline_tag && (
                                <SizableText
                                  size="$2"
                                  color={theme ? "#64748b" : "#94a3b8"}
                                >
                                  {model.pipeline_tag}
                                </SizableText>
                              )}
                            </YStack>
                            <Button
                              size="$2"
                              backgroundColor="transparent"
                              onPress={async () => {
                                try {
                                  await Linking.openURL(`https://huggingface.co/${model.id || model.name}`);
                                } catch (error) {
                                  console.error("Error opening model:", error);
                                }
                              }}
                            >
                              <MaterialIcons name="open-in-new" size={16} color={theme ? "#64748b" : "#94a3b8"} />
                            </Button>
                          </XStack>
                        </Card>
                      ))}
                    </YStack>
                  </ScrollView>
                  {models.length > 5 && (
                    <Button
                      marginTop="$3"
                      backgroundColor={theme ? "#f1f5f9" : "#334155"}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      onPress={async () => {
                        try {
                          await Linking.openURL(`https://huggingface.co/${profile.username}?models`);
                        } catch (error) {
                          console.error("Error opening models page:", error);
                        }
                      }}
                      borderRadius={12}
                      paddingVertical="$2"
                    >
                      <SizableText fontWeight="600" size="$3">
                        View All Models on Hugging Face
                      </SizableText>
                    </Button>
                  )}
                </Card>
              </Animatable.View>
            )}

            {/* Training History */}
            {trainings.length > 0 && (
              <Animatable.View animation="fadeInUp" duration={600} delay={400}>
                <Card
                  backgroundColor={theme ? "#ffffff" : "#1e293b"}
                  borderColor={theme ? "#e2e8f0" : "#334155"}
                  borderRadius={16}
                  padding="$5"
                  elevate
                >
                  <H4
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                    marginBottom="$4"
                  >
                    Training History
                  </H4>
                  <ScrollView maxHeight={400}>
                    <YStack gap="$3">
                      {trainings.map((training: any, index: number) => {
                        const statusColor = 
                          training.status === 'completed' ? '#10b981' :
                          training.status === 'failed' ? '#ef4444' :
                          training.status === 'training' ? '#FFD21E' :
                          '#64748b';
                        
                        return (
                          <Card
                            key={training._id || index}
                            backgroundColor={theme ? "#f8fafc" : "#334155"}
                            padding="$4"
                            borderRadius={12}
                            borderLeftWidth={4}
                            borderLeftColor={statusColor}
                          >
                            <YStack gap="$2">
                              <XStack justifyContent="space-between" alignItems="center">
                                <SizableText
                                  size="$3"
                                  color={theme ? "#1e293b" : "#f1f5f9"}
                                  fontWeight="600"
                                >
                                  {training.modelName || 'Untitled Model'}
                                </SizableText>
                                <View style={{
                                  backgroundColor: statusColor + '20',
                                  paddingHorizontal: 8,
                                  paddingVertical: 4,
                                  borderRadius: 6
                                }}>
                                  <SizableText
                                    size="$2"
                                    color={statusColor}
                                    fontWeight="600"
                                    textTransform="capitalize"
                                  >
                                    {training.status}
                                  </SizableText>
                                </View>
                              </XStack>
                              <SizableText
                                size="$2"
                                color={theme ? "#64748b" : "#94a3b8"}
                              >
                                Started: {new Date(training.startedAt).toLocaleString()}
                              </SizableText>
                              {training.completedAt && (
                                <SizableText
                                  size="$2"
                                  color={theme ? "#64748b" : "#94a3b8"}
                                >
                                  Completed: {new Date(training.completedAt).toLocaleString()}
                                </SizableText>
                              )}
                              {training.duration && (
                                <SizableText
                                  size="$2"
                                  color={theme ? "#64748b" : "#94a3b8"}
                                >
                                  Duration: {Math.round(training.duration / 60)} minutes
                                </SizableText>
                              )}
                              {training.errorMessage && (
                                <SizableText
                                  size="$2"
                                  color="#ef4444"
                                >
                                  Error: {training.errorMessage}
                                </SizableText>
                              )}
                              {training.huggingFaceModelId && (
                                <Button
                                  size="$2"
                                  backgroundColor="transparent"
                                  onPress={async () => {
                                    try {
                                      await Linking.openURL(`https://huggingface.co/${training.huggingFaceModelId}`);
                                    } catch (error) {
                                      console.error("Error opening model:", error);
                                    }
                                  }}
                                  alignSelf="flex-start"
                                  marginTop="$2"
                                >
                                  <XStack alignItems="center" gap="$2">
                                    <MaterialIcons name="open-in-new" size={14} color={theme ? "#64748b" : "#94a3b8"} />
                                    <SizableText size="$2" color={theme ? "#64748b" : "#94a3b8"}>
                                      View Model
                                    </SizableText>
                                  </XStack>
                                </Button>
                              )}
                            </YStack>
                          </Card>
                        );
                      })}
                    </YStack>
                  </ScrollView>
                </Card>
              </Animatable.View>
            )}

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={500}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    Hugging Face Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    You can now export your data models to Hugging Face for training custom AI models.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>

      {/* Export Modal */}
      <HuggingFaceExportModal ref={exportModalRef} />
    </ErrorBoundary>
  );
};

export default HuggingFaceOptions;

